

"use client";
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export const CapTableVisuals = () => {
    const [activeStep, setActiveStep] = useState(0);

    // Auto-cycle through steps
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveStep((prev) => (prev + 1) % 4);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="w-full max-w-[500px] aspect-[4/3] bg-[#ffffff] rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.06)] border border-gray-100 relative overflow-hidden flex flex-col font-jakarta group ring-1 ring-gray-50/50">

            {/* Window Chrome */}
            <div className="h-10 border-b border-gray-50 bg-[#ffffff] flex items-center px-4 justify-between shrink-0 z-20">
                <div className="flex gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57] border border-[#e0443e]/30" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e] border border-[#d89e24]/30" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#28c840] border border-[#1aab29]/30" />
                </div>
                <div className="flex items-center gap-2">
                    <div className="px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100">
                        <span className="text-[9px] font-semibold text-gray-400 tracking-wide uppercase">Cap Table Manager</span>
                    </div>
                </div>
                <div className="w-12" />
            </div>

            {/* Main Content Area */}
            <div className="flex-1 relative overflow-hidden bg-[#ffffff] p-6 lg:p-8">
                <AnimatePresence mode="wait">
                    {activeStep === 0 && <Step1_Info key="step1" />}
                    {activeStep === 1 && <Step2_QA key="step2" />}
                    {activeStep === 2 && <Step3_Table key="step3" />}
                    {activeStep === 3 && <Step4_Download key="step4" />}
                </AnimatePresence>

                {/* Progressive Step Indicator */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-50 flex">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="flex-1 relative h-full">
                            {activeStep === i && (
                                <motion.div
                                    className="absolute inset-0 bg-[#2563eb]"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 5, ease: "linear" }}
                                />
                            )}
                            {activeStep > i && <div className="absolute inset-0 bg-[#2563eb] opacity-30" />}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- Step Components ---

// Step 1: Company Info
const Step1_Info = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full justify-center gap-5"
    >
        <div className="space-y-4 px-4">
            <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Company Name</label>
                <div className="flex items-center gap-2 border-b border-gray-200 pb-1">
                    <span className="text-lg font-bold text-gray-900">Acme Inc.</span>
                </div>
            </div>

            <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Stage</label>
                <div className="flex gap-2">
                    <div className="px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-xs font-bold ring-2 ring-blue-100">Series A</div>
                    <div className="px-3 py-1.5 rounded-lg border border-gray-100 text-gray-400 text-xs font-medium">Seed</div>
                </div>
            </div>

            <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Total Shares</label>
                <div className="text-xl font-mono text-gray-900 tracking-tight">10,000,000</div>
            </div>
        </div>
    </motion.div>
);

// Step 2: Stakeholder Q&A
const Step2_QA = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full gap-4 relative"
    >
        {/* Question Card */}
        <div className="bg-white border border-gray-100 shadow-xl rounded-xl p-4 relative z-10 w-[90%] mx-auto mt-2">
            <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs shrink-0">?</div>
                <div>
                    <div className="text-xs font-bold text-gray-900 mb-1">Add a new stakeholder</div>
                    <div className="text-[10px] text-gray-500 mb-3">Who is investing in this round?</div>

                    <div className="bg-gray-50 rounded-lg p-2 border border-blue-100 flex items-center gap-2">
                        <span className="text-xs text-gray-900 font-medium">Sequoia Capital</span>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="bg-green-100 text-green-700 text-[9px] font-bold px-1.5 py-0.5 rounded"
                        >
                            Investor
                        </motion.div>
                    </div>
                </div>
            </div>

            <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ delay: 1, duration: 2 }}
                className="absolute bottom-0 left-0 h-0.5 bg-blue-500"
            />
        </div>

        {/* Floating Success Toast */}
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="self-center bg-gray-900 text-white px-3 py-1.5 rounded-full text-[10px] font-bold shadow-lg flex items-center gap-2"
        >
            <svg className="w-3 h-3 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
            Added to Cap Table
        </motion.div>
    </motion.div>
);

// Step 3: Real-time Pie Chart View
const Step3_Table = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full bg-[#ffffff] relative"
    >
        <div className="flex items-center justify-between mb-4 px-1 border-b border-gray-50 pb-2">
            <div className="text-xs font-bold text-gray-900">Ownership Split</div>
            <div className="text-[9px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Real-time</div>
        </div>

        <div className="flex items-center gap-6 h-full p-2">
            {/* Pie Chart Visualization */}
            <div className="relative w-32 h-32 flex-shrink-0">
                <motion.div
                    initial={{ rotate: -90, scale: 0 }}
                    animate={{ rotate: 0, scale: 1 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="w-full h-full rounded-full relative"
                    style={{
                        background: `conic-gradient(
                            #2563eb 0% 60%, 
                            #60a5fa 60% 75%, 
                            #93c5fd 75% 100%
                        )`
                    }}
                >
                    {/* Inner White Circle (Donut effect) */}
                    <div className="absolute inset-4 bg-white rounded-full flex items-center justify-center flex-col shadow-inner">
                        <div className="text-[9px] text-gray-400 font-medium">Valuation</div>
                        <div className="text-xs font-bold text-gray-900">$10M</div>
                    </div>
                </motion.div>

                {/* Floating Labels */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}
                    className="absolute -top-2 -right-4 bg-white shadow-md border border-gray-100 rounded px-1.5 py-0.5"
                >
                    <span className="text-[8px] font-bold text-blue-600">60%</span>
                </motion.div>
            </div>

            {/* Legend / Breakdown */}
            <div className="flex-1 space-y-3">
                {[
                    { label: "Founders", pct: "60%", color: "bg-blue-600" },
                    { label: "Investors", pct: "25%", color: "bg-blue-400" },
                    { label: "Option Pool", pct: "15%", color: "bg-blue-300" }
                ].map((item, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + (i * 0.15) }}
                        className="flex items-center justify-between"
                    >
                        <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${item.color}`} />
                            <span className="text-[10px] font-medium text-gray-600">{item.label}</span>
                        </div>
                        <span className="text-[10px] font-bold text-gray-900">{item.pct}</span>
                    </motion.div>
                ))}
            </div>
        </div>
    </motion.div>
);

// Step 4: Download / Export
const Step4_Download = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
        className="flex flex-col h-full items-center justify-center relative"
    >
        {/* File Card */}
        <div className="w-32 aspect-[3/4] bg-white border border-gray-200 rounded-lg shadow-xl relative flex flex-col items-center pt-6 group">
            {/* File Type Icon */}
            <div className="w-10 h-10 bg-green-50 rounded flex items-center justify-center text-green-600 mb-2">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            </div>
            <div className="h-1 w-16 bg-gray-100 rounded mb-1" />
            <div className="h-1 w-12 bg-gray-100 rounded mb-4" />

            <div className="w-full mt-auto border-t border-gray-100 p-2 bg-gray-50 rounded-b-lg">
                <div className="bg-blue-600 text-white text-[8px] font-bold py-1 px-2 rounded text-center shadow-lg transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                    Download .XLSX
                </div>
            </div>

            {/* Corner Fold */}
            <div className="absolute top-0 right-0 w-8 h-8">
                <div className="absolute top-0 right-0 border-t-[32px] border-r-[32px] border-t-white border-r-gray-100 shadow-sm" />
            </div>
        </div>

        {/* Decorative Circles */}
        <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0, 0.3] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="absolute z-[-1] w-48 h-48 bg-blue-100 rounded-full blur-3xl opacity-30"
        />
    </motion.div>
);
